import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bell, Play, X } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { speak, playJingle } from "../lib/speech";

export default function AlarmWatcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [alarm, setAlarm] = useState(null);
  const routinesRef = useRef([]);
  const alertedRef = useRef(JSON.parse(sessionStorage.getItem("rb_alerted") || "{}"));

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const check = () => {
      const now = new Date();
      const nowMinutes = now.getHours() * 60 + now.getMinutes();
      const dayKey = now.toISOString().slice(0, 10);
      for (const r of routinesRef.current) {
        if (!r.alarm_enabled || !r.alarm_time) continue;
        const [h, m] = r.alarm_time.split(":").map(Number);
        if (Number.isNaN(h) || Number.isNaN(m)) continue;
        const alarmMinutes = h * 60 + m;
        // Fire if we're within 30 minutes after the scheduled time (covers
        // throttled background timers and alarms set slightly in the past)
        if (nowMinutes < alarmMinutes || nowMinutes >= alarmMinutes + 30) continue;
        const key = `${r.id}_${dayKey}_${r.alarm_time}`;
        if (alertedRef.current[key]) continue;
        alertedRef.current[key] = true;
        sessionStorage.setItem("rb_alerted", JSON.stringify(alertedRef.current));
        setAlarm(r);
      }
    };

    const tick = async () => {
      try {
        routinesRef.current = (await api.get("/routines")).data;
      } catch { /* keep previous snapshot */ }
      if (!cancelled) check();
    };

    tick();
    const timer = setInterval(tick, 30000);
    const onVisible = () => { if (!document.hidden) tick(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, location.pathname]);

  useEffect(() => {
    if (!alarm) return;
    const msg = alarm.alarm_message || `Time for ${alarm.name}`;
    playJingle(alarm.alarm_sound);
    speak(msg);
    const t = setInterval(() => { playJingle(alarm.alarm_sound); speak(msg); }, 30000);
    return () => clearInterval(t);
  }, [alarm]);

  if (!alarm) return null;

  return (
    <div data-testid="routine-alarm-overlay" className="fixed inset-0 z-[60] bg-background/95 flex items-center justify-center p-6">
      <div className="alarm-flash w-full max-w-md bg-white rounded-3xl border-4 border-primary p-8 text-center">
        <span className="mx-auto w-20 h-20 rounded-full bg-primary/10 grid place-items-center">
          <Bell size={44} strokeWidth={2.5} className="text-primary" />
        </span>
        <h2 className="mt-5 text-3xl font-extrabold">Time for {alarm.name}!</h2>
        <p className="mt-2 text-lg text-muted-foreground font-semibold">Tap start when you're ready.</p>
        <button
          data-testid="alarm-start-button"
          onClick={() => { const r = alarm; setAlarm(null); navigate(`/use/routine/${r.id}`); }}
          className="mt-6 w-full min-h-[64px] rounded-full bg-primary text-white text-xl font-bold flex items-center justify-center gap-2"
        >
          <Play size={26} strokeWidth={3} /> Start now
        </button>
        <button
          data-testid="alarm-stop-button"
          onClick={() => setAlarm(null)}
          className="mt-3 w-full min-h-[56px] rounded-full border-2 border-foreground/25 bg-white text-lg font-bold flex items-center justify-center gap-2 hover:bg-secondary transition-colors duration-200"
        >
          <X size={22} strokeWidth={2.5} /> Stop
        </button>
      </div>
    </div>
  );
}
